/**
 * `DonutLegend` — a donut's lightbox legend (launch round, 2026-10-07): one line per slice
 * with its swatch, name, value and share of the whole, and the total under them.
 *
 * A donut has no axes to label, so this is what its expanded view adds instead: every number
 * the slices only showed on hover, written down at once.
 */
import { useTranslation } from 'react-i18next';

import { fmtNum } from '../../lib/format';
import type { DonutItem } from './Donut';

export interface DonutLegendProps {
  items: readonly DonutItem[];
  /** §G3 decimals for the values. */
  fmt?: number;
  /** Appended to each value and the total (`ha`, `%`), when the values carry one. */
  unit?: string;
}

export function DonutLegend({ items, fmt = 0, unit = '' }: DonutLegendProps) {
  const { t } = useTranslation();
  const drawn = items.filter((item) => Number.isFinite(item.value) && item.value > 0);
  const total = drawn.reduce((sum, item) => sum + item.value, 0);
  const withUnit = (v: number) => `${fmtNum(v, fmt)}${unit ? ` ${unit}` : ''}`;
  return (
    <div className="donut-legend" data-testid="donut-legend">
      <ul>
        {drawn.map((item) => (
          <li key={item.label}>
            <span className="donut-legend-swatch" style={{ background: item.color }} aria-hidden="true" />
            <span className="donut-legend-name">{item.label}</span>
            <span className="donut-legend-val">{withUnit(item.value)}</span>
            <span className="donut-legend-pct">{total > 0 ? `${fmtNum((item.value / total) * 100, 1)}%` : ''}</span>
          </li>
        ))}
      </ul>
      <p className="donut-legend-total">
        <span>{t('charts.total')}</span>
        <b data-testid="donut-legend-total">{withUnit(total)}</b>
      </p>
    </div>
  );
}

export default DonutLegend;
